import { queryClient } from '@/utils/query/queryClient';
import { librarySectionsQueryOptions } from '@/hooks/queries/useLibrarySectionsQuery';
import resolvePresetBySlug from '@/requests/library/resolvePresetBySlug';
import { setAppStore } from '@/store';
import { Screen } from '@/types';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { AiGenScreenAction, ScreenType } from '@/types/analytics';

interface TryOpenPresetFeedFromDeeplinkParams {
  slug: string;
  category?: string;
}

/**
 * Feed entry preparation for the preset deeplink handler (runs outside
 * React); the click-entry counterpart of `usePresetFeedOpenAnalytics`.
 * Returns `true` only when it routed to the feed. With a URL category this is synchronous (no requests before
 * first paint); without one it costs a preset fetch to learn the category —
 * that fetch is cached under `['libraryItemInfo', slug]` (the key the feed's
 * `loadActivePreset` reads), so the entry slide is already warm on mount
 * instead of being fetched a second time.
 * If the entry slug turns out not to be in the category's list, the feed
 * opens on its first slide.
 */
const tryOpenPresetFeedFromDeeplink = async ({
  slug,
  category,
}: TryOpenPresetFeedFromDeeplinkParams): Promise<boolean> => {
  let feedCategory = category;
  if (!feedCategory) {
    const preset = await resolvePresetBySlug(slug, queryClient).catch(
      () => null
    );
    feedCategory = preset?.category;
  }
  if (!feedCategory) return false;

  queryClient.prefetchQuery(librarySectionsQueryOptions);

  sendAiGenScreenActionAnalytics({
    action: AiGenScreenAction.OPEN,
    screen_type: ScreenType.PRESET_FEED,
    source: 'deeplink',
    settings: { category: feedCategory, id: slug },
  });

  setAppStore({
    activeTabSlug: feedCategory,
    shouldScrollToActiveTab: true,
    presetFeedEntrySlug: slug,
    screen: Screen.Create,
  });
  return true;
};

export default tryOpenPresetFeedFromDeeplink;
